import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { listProfitEntries, deleteProfitEntry, bulkDeleteProfitEntries } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { Th, MONEY_COLS, MoneyCells, ProfitCell } from "./ProfitUI";
import { ScopeSelect, scopeParam } from "./ScopeSelect";

const PAGE_SIZE = 20;

export function HistoryTab() {
  const { branchId } = useBranch();
  const [scope, setScope] = useState("current");
  const [range, setRange] = useState({ start_date: "", end_date: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marked, setMarked] = useState(new Set());

  const reqId = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const res = await listProfitEntries({ page, page_size: PAGE_SIZE, ...range, ...scopeParam(scope) });
      if (id !== reqId.current) return;
      if (res.pages > 0 && page > res.pages) return setPage(res.pages);
      setData(res);
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [page, range, scope]);

  useEffect(() => { load(); }, [load, branchId]);
  useEffect(() => { setPage(1); setMarked(new Set()); }, [range, scope, branchId]);
  useEffect(() => setMarked(new Set()), [page]);

  const remove = async (id) => {
    if (!window.confirm("Delete this profit entry?")) return;
    await deleteProfitEntry(id);
    toast.success("Entry deleted");
    load();
  };

  const items = data?.items || [];
  const cols = MONEY_COLS.length + 5;
  const allMarked = items.length > 0 && items.every((r) => marked.has(r.id));
  const toggle = (id) => setMarked((m) => { const n = new Set(m); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setMarked(allMarked ? new Set() : new Set(items.map((r) => r.id)));

  const removeMarked = async () => {
    if (!window.confirm(`Delete ${marked.size} marked entr${marked.size === 1 ? "y" : "ies"}?`)) return;
    const res = await bulkDeleteProfitEntries([...marked]);
    toast.success(`${res.deleted} entr${res.deleted === 1 ? "y" : "ies"} deleted`);
    setMarked(new Set());
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <ScopeSelect value={scope} onChange={setScope} testId="history-scope-select" />
        {["start_date", "end_date"].map((k) => (
          <div key={k} className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">{k === "start_date" ? "From" : "To"}</Label>
            <Input type="date" className="w-40" value={range[k]} onChange={(e) => setRange((r) => ({ ...r, [k]: e.target.value }))} data-testid={`history-${k.replace("_", "-")}-input`} />
          </div>
        ))}
        {marked.size > 0 && (
          <Button size="sm" onClick={removeMarked} className="ml-auto bg-rose-600 hover:bg-rose-700 text-white" data-testid="history-delete-marked-btn">
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete marked ({marked.size})
          </Button>
        )}
      </div>
      <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="profit-history-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th><Checkbox checked={allMarked} onCheckedChange={toggleAll} disabled={!items.length} data-testid="history-mark-all" /></Th>
              <Th>Date</Th><Th>Branch</Th>
              {MONEY_COLS.map(([k, l]) => <Th key={k} className="text-right">{l}</Th>)}
              <Th className="text-right">Final Profit</Th><Th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={cols} className="px-4 py-6 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={cols} className="px-4 py-6 text-center text-zinc-500" data-testid="history-empty">No profit entries found.</td></tr>}
            {!loading && items.map((r) => (
              <tr key={r.id} className={`border-b border-zinc-100 last:border-b-0 tabular ${marked.has(r.id) ? "bg-rose-50/60" : ""}`} data-testid={`history-row-${r.id}`}>
                <td className="px-3 py-2.5"><Checkbox checked={marked.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`history-mark-${r.id}`} /></td>
                <td className="px-3 py-2.5 font-medium whitespace-nowrap">{r.entry_date}</td>
                <td className="px-3 py-2.5 text-zinc-600 whitespace-nowrap">{r.branch_name}</td>
                <MoneyCells row={r} />
                <ProfitCell value={r.final_profit} testId={`history-profit-${r.id}`} />
                <td className="px-2"><Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`history-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span data-testid="history-count">{data?.total || 0} entries · page {data?.page || 1} of {Math.max(data?.pages || 1, 1)}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="history-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" disabled={page >= (data?.pages || 1)} onClick={() => setPage((p) => p + 1)} data-testid="history-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
